import React, { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Button, Flex, QueryLoader, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import {
  useGetPositionNameInfoQuery,
  useGpPositionPageDetailsQuery,
} from "../../../../graphql/supergraphHooks";
import { toGlobalId } from "../../../../utils/graphql_utils";
import { provideLegalName } from "../../../ProfileTree/utils";
import { ComplianceActivityCard } from "../../ComplianceActivityCard";
import { DashBoardHeader } from "../../DashBoardHeader";
import { DashboardTabs } from "../DashboardTabs";
import { useDashboardNavigation } from "../useDashboardNavigation";
import { useDashboardTab } from "../useDashboardTab";
import { PositionActions } from "./PositionActions";
import { PositionSummary } from "./PositionSummary";
import { onboardingStateText, onboardingStateWrapper } from "./styles_vanilla.css";
import type { HierarchyChildProfile } from "./types";

type Props = { arenaId: number; arenaName?: string };
type LocationState = { entityName?: string };

export const PositionDashboard: React.FC<Props> = ({ arenaId, arenaName }) => {
  const [activeTab, setActiveTab] = useDashboardTab();
  const { eid, pid } = useParams<{ eid: string; pid: string }>();
  const entityId = Number(eid);
  const positionId = Number(pid);
  const { state } = useLocation<LocationState>();
  const { navigateToArena, navigateToEntity } = useDashboardNavigation();

  const result = useGpPositionPageDetailsQuery({
    fetchPolicy: "cache-and-network",
    variables: { arenaId, entityId, positionId },
  });

  const positionInfoResult = useGetPositionNameInfoQuery({
    variables: {
      positionFilters: {
        arenaIds: [toGlobalId("ArenaObjectType", arenaId)],
        entityIds: [toGlobalId("AccountObjectType", entityId)],
        positionIds: [toGlobalId("PositionObjectType", positionId)],
      },
    },
  });

  const positionNode = useMemo(() => result.data?.compliancePositions?.[0], [result.data]);
  const positionInfoNode = useMemo(
    () => positionInfoResult.data?.positions?.edges?.[0]?.node,
    [positionInfoResult.data],
  );

  const entityName =
    positionInfoNode?.investmentEntity?.name ?? positionNode?.entityName ?? state?.entityName;

  const positionName = useMemo(
    () => positionInfoNode?.account?.name ?? provideLegalName(positionNode?.investingProfile),
    [positionInfoNode?.account?.name, positionNode?.investingProfile],
  );

  const rootProfile = useMemo(
    () => positionNode?.investingProfile,
    [positionNode?.investingProfile],
  );

  const childProfiles = useMemo(
    () => (positionNode?.childProfiles ?? []).filter((p: HierarchyChildProfile) => Boolean(p)),
    [positionNode?.childProfiles],
  );

  return (
    <Flex flexDirection="column">
      <DashBoardHeader
        config={{
          variant: "position",
          entityName: entityName,
          positionName,
          onRootClick: navigateToArena,
          onEntityClick: () => navigateToEntity(entityId, entityName),
        }}
        tabs={
          <DashboardTabs
            activeTab={activeTab}
            onChange={setActiveTab}
            auditLogDisabled={!positionNode}
          />
        }
        actions={
          <PositionActions arenaId={arenaId} positionId={positionId} disabled={!positionNode} />
        }
      />

      <QueryLoader result={[result, positionInfoResult]}>
        {positionNode ? (
          <Flex flexDirection="column" gap={Sizes.LG}>
            {activeTab === "summary" ? (
              <PositionSummary
                positionName={positionName}
                rootProfile={rootProfile}
                childProfiles={childProfiles}
                positionNode={positionNode}
                arenaId={arenaId}
                arenaName={arenaName}
                positionId={positionId}
                onViewAllAudit={() => setActiveTab("auditLog")}
              />
            ) : (
              <ComplianceActivityCard
                arenaId={arenaId}
                arenaName={arenaName}
                positionId={positionId}
              />
            )}
          </Flex>
        ) : (
          <Flex flexDirection="column" gap={Sizes.MD} className={onboardingStateWrapper}>
            <Text variant="h3">This position is being onboarded</Text>
            <Text variant="regular-md" className={onboardingStateText}>
              Please check back shortly for updates.
            </Text>
            <Button variant="primary" onClick={() => navigateToEntity(entityId, entityName)}>
              {entityName ? `Back to ${entityName}` : "Back"}
            </Button>
          </Flex>
        )}
      </QueryLoader>
    </Flex>
  );
};
